import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getRankInfo, getRankIcon, isErRank, getTPToNextRank, calculateRankFromTP } from '../lib/ranks';

interface RankTooltipProps {
  rank: number;
  tp?: number;
  username?: string;
}

export default function RankTooltip({ rank, tp = 0, username }: RankTooltipProps) {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  
  // Config yüklenene kadar bekle
  useEffect(() => {
    const loadConfig = async () => {
      await import('../lib/ranks').then(m => m.ensureRankConfigLoaded?.());
      setIsLoaded(true);
    };
    loadConfig();
  }, []);
  
  // TP'den gerçek rütbeyi hesapla (kullanıcının mevcut TP'sine göre)
  const actualRank = calculateRankFromTP(tp);
  const rankInfo = getRankInfo(actualRank);
  const rankIcon = getRankIcon(actualRank);
  const { nextRank, tpNeeded, progress } = getTPToNextRank(tp);
  const isEr = isErRank(actualRank);

  useEffect(() => {
    if (show && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      
      setPosition({
        top: rect.bottom + scrollY + 8,
        left: rect.left + rect.width / 2,
      });
    }
  }, [show]);

  const tooltipContent = show && (
    <div
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        transform: 'translateX(-50%)',
        minWidth: 200,
        maxWidth: 280,
        padding: 16,
        background: '#1a1a1a',
        border: '2px solid #114232',
        borderRadius: 12,
        boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
        zIndex: 999999,
        pointerEvents: 'auto',
      }}
    >
      {/* Rütbe ismi */}
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>
          {isEr ? (
            <span>🟢</span>
          ) : (
            <img 
              src={rankIcon} 
              alt={rankInfo.name}
              style={{ 
                width: 64, 
                height: 64, 
                objectFit: 'contain',
                margin: '0 auto',
              }}
            />
          )}
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#114232' }}>
          {rankInfo.name}
        </div>
        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
          Seviye {rankInfo.level}
        </div>
      </div>

      {/* TP bilgisi */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: '#fff', marginBottom: 4 }}>
          <span style={{ fontWeight: 600 }}>Toplam TP:</span> {tp.toFixed(1)}
        </div>
        <div style={{ fontSize: 12, color: '#888' }}>
          Minimum: {rankInfo.minTP} TP
        </div>
      </div>

      {/* İlerleme */}
      {nextRank && (
        <>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>
            Bir sonraki rütbe:
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <div style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {isErRank(nextRank.level) ? (
                <span style={{ fontSize: 20 }}>🟢</span>
              ) : (
                <img 
                  src={getRankIcon(nextRank.level)} 
                  alt={nextRank.name}
                  style={{ 
                    width: 24, 
                    height: 24, 
                    objectFit: 'contain',
                  }}
                />
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
                {nextRank.name}
              </div>
              <div style={{ fontSize: 11, color: '#888' }}>
                {tpNeeded.toFixed(1)} TP kaldı
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{
            width: '100%',
            height: 6,
            background: '#333',
            borderRadius: 3,
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${progress}%`,
              height: '100%',
              background: 'linear-gradient(to right, #114232, #1a5a42)',
              transition: 'width 0.3s ease',
            }}></div>
          </div>
          <div style={{ fontSize: 11, color: '#888', textAlign: 'right', marginTop: 4 }}>
            %{progress.toFixed(0)}
          </div>
        </>
      )}

      {!nextRank && (
        <div style={{ 
          fontSize: 13, 
          color: '#FFD700', 
          textAlign: 'center',
          fontWeight: 600,
          marginTop: 8,
        }}>
          🏆 Maksimum Rütbe!
        </div>
      )}

      {/* Kullanıcı adı */}
      {username && (
        <div style={{ 
          fontSize: 11, 
          color: '#666', 
          textAlign: 'center',
          marginTop: 12,
          paddingTop: 8,
          borderTop: '1px solid #333',
        }}>
          {username}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <div 
          ref={triggerRef}
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
          style={{
            cursor: 'pointer',
            fontSize: 'inherit',
            lineHeight: 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title={`${rankInfo.name} - Rütbe bilgisi`}
        >
          {isEr ? (
            <span style={{ fontSize: '1em' }}>🟢</span>
          ) : (
            <img 
              src={rankIcon} 
              alt={rankInfo.name}
              style={{ 
                width: '1em', 
                height: '1em', 
                objectFit: 'contain',
                display: 'block',
              }}
            />
          )}
        </div>
      </div>
      
      {tooltipContent && createPortal(tooltipContent, document.body)}
    </>
  );
}
